import ConfirmDialog from "@Components/admin/confirm-dialog";
import Icon from "@Components/generic/icon";
import { teamAColor, teamBColor } from "@Context/game/builder/builder-context";
import { faBars, faCheck, faCopy, faPaste, faPause, faPlay, faRedo, faUndo, faWarning } from "@fortawesome/pro-regular-svg-icons";
import useBuilderDecisions from "@hooks/game/builder/use-builder-decisions";
import { useScenarioRecovery } from "@hooks/game/builder/use-scenario-recovery";
import { useScenarioBuilder } from "@hooks/index";
import useI18n from "@i18n";
import { Box, Button, Paper, Stack, TextField, Typography, styled, useTheme } from "@mui/material";
import "@pixi/events";
import { round } from "lodash-es";
import { useEffect, useState } from "react";
import { useLocation } from "react-use";
import ActionMenu from "./action-menu";
import BuilderMenu from "./builder-menu";
import FieldMenu from "./field-menu";
import { ScenarioClip, WarningSeverity } from "./models/builder-data-types";
import PhysicsPromptDialog from "./physics-prompt-dialog";
import PlayerMenu from "./player-menu";
import { BuilderNavData } from "./standalone-builder";
import Timeline from "./timeline";
import Viewport from "./viewport";
import WarningList from "./warning-list";
import WaypointMenu from "./waypoint-menu";

export const Builder = ({
  clip,
  onClipChange,
  showActionBall,
  showTrackedBall,
  showRawTrackedBall,
}: {
  clip: ScenarioClip;
  onClipChange?: (clip: ScenarioClip) => void;
  showActionBall?: boolean;
  showTrackedBall?: boolean;
  showRawTrackedBall?: boolean;
}) => {
  const {
    selectedPlayer,
    isPlaying,
    scenarioMetadata,
    viewportDimensions,
    setScenarioMetadata,
    handleUndo,
    handleRedo,
    handlePlay,
    pausePlayback,
    handleCopyClick,
    handlePasteClick,
    state,
    dispatch,
  } = useScenarioBuilder({ clip, onClipChange });
  const [showDecisionCards, setShowDecisionCards] = useState(false);

  const { decisionSnapshot, autoAssignDecisionScores, autoAssignLoading } = useBuilderDecisions({
    isPlaying,
    enabled: showDecisionCards,
    pausePlayback,
  });

  const { i18n } = useI18n();
  const locationState = (useLocation().state as BuilderNavData) || undefined;

  useEffect(() => {
    setScenarioMetadata(clip.metadata || locationState?.scenario || undefined);
  }, [clip.metadata, locationState, setScenarioMetadata]);

  const { recoveryDraft, recoverySavedAt, restoreRecoveryDraft, discardRecoveryDraft } = useScenarioRecovery({
    scenarioMetadata,
    state,
    dispatch,
  });

  const theme = useTheme();

  const StyledTextField = styled(TextField)({
    "& .MuiInputBase-root": {
      color: theme.palette.dsBase.v200,
      fontSize: "16px",
      height: "38px",
    },
    "& .MuiInput-underline:before": {
      borderBottomColor: theme.palette.dsBase.v200,
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottomColor: theme.palette.dsBase.v200,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: theme.palette.dsBase.v200,
    },
  });

  return (
    <Paper
      elevation={1}
      sx={{
        overflow: "hidden",
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Viewport
          width={viewportDimensions.width}
          height={viewportDimensions.height}
          showActionBall={showActionBall}
          showTrackedBall={showTrackedBall}
          showRawTrackedBall={showRawTrackedBall}
          showDecisionCards={showDecisionCards}
          setShowDecisionCards={setShowDecisionCards}
          decisionSnapshot={decisionSnapshot}
          onAutoAssignDecisionScores={autoAssignDecisionScores}
          autoAssignLoading={autoAssignLoading}
        />
      </Box>
      <Box
        sx={{
          height: state.timelineHeight,
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <Timeline />
        <Stack spacing={2} alignContent={"center"} alignItems={"center"} sx={{ overflow: "hidden" }}>
          <Stack direction="row" spacing={2} alignItems={"center"}>
            <Stack direction="row" spacing={2} alignItems={"center"} sx={{ position: "absolute", left: "25px" }}>
              <Typography color={theme.palette.dsBase.v200}>{i18n("builder.selected")}</Typography>
              {selectedPlayer ? (
                <Typography
                  sx={{ fontWeight: 800, width: "200px", textTransform: "capitalize" }}
                  color={selectedPlayer.team == 1 ? teamAColor : teamBColor}
                >
                  {selectedPlayer.name}
                </Typography>
              ) : (
                <Typography sx={{ fontWeight: 800, width: "200px" }} color={theme.palette.dsNeutral.v300}>
                  {i18n("builder.selected.none")}
                </Typography>
              )}
            </Stack>
            <Button variant="outlined" onClick={() => dispatch({ showBuilderMenu: true })} sx={{ paddingX: "0px" }}>
              &nbsp;
              <Icon icon={faBars} />
              &nbsp;
            </Button>{" "}
            {/* Forgive my HTML crimes */}
            <Button variant="outlined" sx={{ width: "110px" }} onClick={handlePlay}>
              &nbsp;
              <Icon icon={isPlaying ? faPause : faPlay} />
              &nbsp;
            </Button>
            <Button
              variant="outlined"
              sx={{ width: "100px" }}
              disabled={!selectedPlayer || !selectedPlayer.actions[state.selectedAction]}
              onClick={handleCopyClick}
            >
              &nbsp;
              <Icon icon={faCopy} />
              &nbsp;
            </Button>
            <Button variant="outlined" sx={{ width: "100px" }} disabled={!state.copiedAction} onClick={handlePasteClick}>
              &nbsp;
              <Icon icon={faPaste} />
              &nbsp;
            </Button>
            <Button variant="outlined" sx={{ width: "100px" }} disabled={state.clip.undoStates.length <= 1} onClick={handleUndo}>
              &nbsp;
              <Icon icon={faUndo} />
              &nbsp;
            </Button>
            <Button variant="outlined" sx={{ width: "100px" }} disabled={state.clip.redoStates.length === 0} onClick={handleRedo}>
              &nbsp;
              <Icon icon={faRedo} />
              &nbsp;
            </Button>
            {state.warnings.length > 0 ? (
              <Button
                variant="contained"
                color={state.warnings.some(w => w.severity === WarningSeverity.Critical) ? "error" : "warning"}
                sx={{ width: "100px" }}
                onClick={() => {
                  dispatch({ showWarnings: true });
                }}
              >
                &nbsp;
                <Icon icon={faWarning} />
                &nbsp;
              </Button>
            ) : (
              <Button variant="outlined" color="success" disabled sx={{ width: "100px" }}>
                &nbsp;
                <Icon icon={faCheck} />
                &nbsp;
              </Button>
            )}
            <Stack direction="row" spacing={2} alignItems={"center"} sx={{ position: "absolute", right: "25px" }}>
              <Typography color={theme.palette.dsBase.v200}>{i18n("builder.time")}</Typography>
              <Typography color={theme.palette.dsBase.v200} sx={{ width: "35px", textAlign: "right" }}>
                {round(state.time, 2)}
              </Typography>
              <Typography color={theme.palette.dsBase.v200}>/</Typography>
              <StyledTextField
                type="number"
                value={state.clip.scenario.endTime}
                onChange={e =>
                  dispatch({
                    clip: {
                      ...state.clip,
                      scenario: {
                        ...state.clip.scenario,
                        endTime: parseFloat(e.target.value),
                      },
                    },
                  })
                }
                sx={{ width: "80px" }}
              />
            </Stack>
          </Stack>
        </Stack>
      </Box>

      <PlayerMenu />
      <WaypointMenu />
      <FieldMenu />
      <ActionMenu />
      <BuilderMenu scenarioMetadata={scenarioMetadata} setScenarioMetadata={setScenarioMetadata} />
      <WarningList />
      <PhysicsPromptDialog />
      {recoveryDraft && (
        <ConfirmDialog
          open={!!recoveryDraft}
          confirm={restoreRecoveryDraft}
          cancel={discardRecoveryDraft}
          title={i18n("builder.recovery.title")}
          message={i18n("builder.recovery.message", { savedAt: recoverySavedAt })}
          confirmText={i18n("builder.recovery.restore")}
          cancelText={i18n("builder.recovery.discard")}
          icon={undefined}
        />
      )}
    </Paper>
  );
};

export default Builder;
